import { useTranslation } from 'react-i18next';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { Card, CardHeader, CardContent } from '@cortex/web-design-system/components/ui/Card';
import { useCombatPowerHistory } from '../../../hooks/useCombatPower.js';

interface CombatPowerChartProps {
  nickName: string | null;
}

/** @graph-connects none */
export function CombatPowerChart({ nickName }: CombatPowerChartProps) {
  const { t } = useTranslation();
  const { data, isLoading } = useCombatPowerHistory(nickName ?? undefined);

  if (!nickName) return null;

  if (isLoading) {
    return (
      <Card className="animate-content-reveal">
        <CardHeader title={t('members.combatPower.historyTitle')} />
        <CardContent>
          <div className="h-48 flex items-center justify-center text-[var(--color-text-muted)] animate-pulse">
            {t('status.loading')}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!data || data.months.length === 0) return null;

  const chartData = data.months.map((m) => ({
    month: m.month, // keep full "2025/07" format
    expected: m.expected,
    actual: m.actual,
  }));

  return (
    <Card className="animate-content-reveal">
      <CardHeader title={t('members.combatPower.historyTitle')} />
      <CardContent>
        <div className="h-56">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
              <XAxis
                dataKey="month"
                tick={{ fontSize: 10, fill: 'var(--color-text-muted)' }}
                angle={-30}
                textAnchor="end"
                height={45}
              />
              <YAxis tick={{ fontSize: 11, fill: 'var(--color-text-muted)' }} />
              <Tooltip
                wrapperStyle={{ zIndex: 50, outline: 'none' }}
                contentStyle={{
                  backgroundColor: 'var(--color-bg-elevated)',
                  border: '1px solid var(--color-border)',
                  borderRadius: '8px',
                  fontSize: '12px',
                  boxShadow: 'var(--shadow-lg)',
                }}
                formatter={
                  // 戦闘力は小数第1位まで表示
                  ((value: number) =>
                    typeof value === 'number' ? value.toFixed(1) : String(value)) as never
                }
              />
              <Legend wrapperStyle={{ fontSize: '12px' }} />
              <Line
                type="monotone"
                dataKey="expected"
                name={t('members.combatPower.expectedLabel')}
                stroke="#6366f1"
                strokeWidth={2}
                dot={{ r: 3 }}
                connectNulls
              />
              <Line
                type="monotone"
                dataKey="actual"
                name={t('members.combatPower.actualLabel')}
                stroke="#f59e0b"
                strokeWidth={2}
                dot={{ r: 3 }}
                connectNulls
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
}
